import type { Locale, StudentProfileContract } from "@chams-pay/contracts";
import {
  normalizeSchoolLevelLabel,
  operationsMessages,
  paymentMessages,
  translate,
} from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button, buttonVariants } from "@chams-pay/design-system/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@chams-pay/design-system/components/dialog";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import { FinancialDocument, printFinancialDocument } from "@chams-pay/document-ui";
import {
  Banknote,
  ChevronLeft,
  Clock,
  Copy,
  Landmark,
  LoaderCircle,
  MessageCircle,
  Printer,
  Wallet,
} from "lucide-react";
import { periodLabel } from "../core/format.js";
import type { PaymentController } from "../core/use-payment.js";
import { A4DocumentPreview } from "../ui/a4-document-preview.js";
import { remainingAfterAllocation, useCountUp } from "../core/use-payment.js";
import { usePreparedWhatsAppIntent } from "../core/use-whatsapp-intent.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, Count, DateValue } from "../ui/figures.js";
import { ReceiptLanguageSwitch } from "./receipt-language-switch.js";
import { PeriodGrid } from "./surfaces.js";

const stepKeys = ["sp.stepA", "sp.stepB", "sp.stepC", "sp.stepD", "sp.stepE"] as const;

function Stepper({ locale, current }: { readonly locale: Locale; readonly current: number }) {
  return (
    <div className="payment-steps mt-4 flex items-center gap-2">
      {([1, 2, 3, 4, 5] as const).map((number) => (
        <div key={number} className="contents">
          {number > 1 && (
            <span
              aria-hidden="true"
              data-complete={current > number - 1}
              className="h-0.5 flex-1 rounded-full bg-border transition-colors duration-slow data-[complete=true]:bg-primary"
            />
          )}
          <span
            data-current={current >= number}
            className="grid size-[26px] shrink-0 place-items-center rounded-full bg-muted font-heading text-[11px] font-extrabold text-muted-foreground transition-colors duration-base data-[current=true]:bg-primary data-[current=true]:text-primary-foreground"
          >
            <Count value={number} />
          </span>
        </div>
      ))}
      <strong className="ms-auto font-heading text-[11.5px] text-muted-foreground">
        {translate(locale, stepKeys[current - 1] ?? "sp.stepA")}
      </strong>
    </div>
  );
}

function SummaryRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
      <span>{label}</span>
      <strong className="font-heading text-foreground">{children}</strong>
    </div>
  );
}

export function DesktopPaymentFlow({
  locale,
  online,
  csrf,
  student,
  payment,
  yearContext,
  synchronize,
  open,
  onOpenChange,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string;
  readonly student: StudentProfileContract;
  readonly payment: PaymentController;
  readonly yearContext?: { readonly role: "historical" | "next"; readonly academicYear: string };
  readonly synchronize: () => Promise<void>;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const text = paymentMessages(locale);
  const operationsText = operationsMessages(locale);
  const running = useCountUp(payment.allocatedNumeric);
  const paidPeriodLabels = payment.selectedPeriodEntries
    .filter((entry) =>
      entry.obligations.some((obligation) =>
        payment.allocations.some(
          (allocation) =>
            allocation.obligationId === obligation.id && /^[1-9][0-9]*$/.test(allocation.amountMad),
        ),
      ),
    )
    .map((entry) => ({ period: entry.period, label: periodLabel(entry.period, locale) }));
  const cash = payment.draft.method === "cash";
  const queuedOffline =
    !online && (payment.result?.queueState === "queued" || payment.result?.queueState === "failed");
  const whatsapp = usePreparedWhatsAppIntent({
    paymentId: payment.result?.payment.id ?? null,
    locale: payment.receiptLocale,
    csrf,
    enabled: open && payment.step === "document" && online,
    beforePrepare: synchronize,
  });

  const close = () => {
    onOpenChange(false);
    payment.restart();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        className="payment-flow-surface flex max-h-[88dvh] w-full flex-col gap-0 p-0 sm:max-w-[780px]"
        closeLabel={text.close}
        data-testid="new-payment-surface"
      >
        <DialogHeader className="flex-none border-b border-border/70 px-6 pb-4 pt-5">
          <DialogTitle className="font-heading text-lg">
            {t(
              yearContext?.role === "next"
                ? "sp.takeNextYearPayment"
                : yearContext?.role === "historical"
                  ? "sp.takeHistoricalYearPayment"
                  : "sp.takePayment",
            )}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            {locale === "ar" ? student.nameAr : student.nameFr} ·{" "}
            <Bidi>
              {student.internalReference} · {normalizeSchoolLevelLabel(student.currentClass)}
            </Bidi>
          </DialogDescription>
          <Stepper locale={locale} current={payment.stepNumber} />
        </DialogHeader>

        <div
          className="payment-flow flex-1 overflow-y-auto px-6 py-5"
          data-testid="payment-workflow"
        >
          {payment.step === "selection" && (
            <section key="selection" className="payment-step animate-fade-up flex flex-col gap-4">
              <p className="payment-step__heading text-[12.5px] leading-relaxed text-muted-foreground">
                {t("sp.step1Body")}
              </p>
              <PeriodGrid
                locale={locale}
                entries={payment.periods}
                selected={payment.selectedPeriods}
                onToggle={payment.togglePeriod}
                label={t("sp.stepA")}
              />
            </section>
          )}

          {payment.step === "allocation" && (
            <section key="allocation" className="payment-step animate-fade-up flex flex-col gap-4">
              <p className="payment-step__heading text-[12.5px] leading-relaxed text-muted-foreground">
                {t("sp.step2Body")}
              </p>
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="flex items-center gap-3 border-b border-border/70 bg-surface-2 px-4 py-3">
                  <span className="chams-eyebrow flex-1">{t("sp.period")}</span>
                  <span className="chams-eyebrow w-24">{t("sp.remaining")}</span>
                  <span className="chams-eyebrow w-32">{t("sp.allocated")}</span>
                </div>
                <div className="allocation-list flex flex-col">
                  {payment.selectedPeriodEntries.flatMap((entry) =>
                    entry.obligations.map((obligation) => {
                      const allocation = payment.allocations.find(
                        (item) => item.obligationId === obligation.id,
                      );
                      const label = locale === "ar" ? obligation.labelAr : obligation.labelFr;
                      const remaining = remainingAfterAllocation(
                        obligation.remainingMad,
                        allocation?.amountMad ?? "0",
                      );
                      return (
                        <div
                          key={obligation.id}
                          className="flex items-start gap-3 border-b border-border/50 px-4 py-2.5 last:border-b-0"
                        >
                          <span className="flex min-w-0 flex-1 flex-col">
                            <strong className="truncate font-heading text-[12.5px]">{label}</strong>
                            <small className="text-[10.5px] text-muted-foreground">
                              {periodLabel(entry.period, locale)}
                            </small>
                          </span>
                          <span className="w-24 font-heading text-[12.5px] text-muted-foreground">
                            <Amount value={obligation.remainingMad} locale={locale} />
                          </span>
                          <span className="flex w-32 flex-col gap-1">
                            <Input
                              className="chams-figure h-11 w-32 text-end"
                              inputMode="numeric"
                              dir="ltr"
                              value={allocation?.amountMad ?? "0"}
                              aria-label={`${text.allocated} ${label}`}
                              onChange={(event) =>
                                payment.setAllocation(obligation.id, event.target.value)
                              }
                            />
                            <small
                              className={`text-end font-heading text-[10px] ${
                                remaining === null ? "text-destructive" : "text-muted-foreground"
                              }`}
                            >
                              {t("sp.remaining")}{" "}
                              {remaining === null ? (
                                t("sp.invalid")
                              ) : (
                                <Amount value={remaining} locale={locale} />
                              )}
                            </small>
                          </span>
                        </div>
                      );
                    }),
                  )}
                </div>
                <div
                  className="allocation-total flex items-center gap-3 border-t border-border/70 bg-surface-2 px-4 py-3"
                  data-valid={payment.allocationsValid}
                >
                  <span className="flex-1 font-heading text-[12.5px] font-bold">
                    {t("sp.total")}
                  </span>
                  <strong className="font-heading text-[17px] text-primary">
                    <Amount value={payment.allocatedNumeric} locale={locale} />
                  </strong>
                  <Badge dot variant={payment.allocationsValid ? "success" : "destructive"}>
                    {t(payment.allocationsValid ? "sp.valid" : "sp.invalid")}
                  </Badge>
                </div>
              </div>
            </section>
          )}

          {payment.step === "details" && (
            <section key="details" className="payment-step animate-fade-up flex flex-col gap-4">
              <div className="method-picker grid grid-cols-3 gap-2.5" role="radiogroup">
                {(
                  [
                    { value: "cash", icon: <Wallet /> },
                    { value: "cheque", icon: <Banknote /> },
                    { value: "transfer", icon: <Landmark /> },
                  ] as const
                ).map((entry) => (
                  <button
                    key={entry.value}
                    type="button"
                    role="radio"
                    aria-checked={payment.draft.method === entry.value}
                    data-active={payment.draft.method === entry.value}
                    onClick={() => payment.patchDraft({ method: entry.value })}
                    className="flex min-h-[76px] flex-col items-center justify-center gap-2 rounded-lg border-[1.5px] border-border bg-card font-heading text-[12.5px] font-bold text-muted-foreground transition-colors duration-fast hover:border-teal-300 data-[active=true]:border-primary data-[active=true]:bg-teal-50 data-[active=true]:text-primary-strong [&_svg]:size-5"
                  >
                    {entry.icon}
                    {text[entry.value]}
                  </button>
                ))}
              </div>

              {payment.draft.method === "cheque" && (
                <div className="payment-fields animate-fade-up flex flex-col gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Label className="flex flex-col items-stretch gap-1.5 text-start">
                      <span className="text-[11.5px] font-bold text-foreground/70">
                        {text.chequeNumber}
                      </span>
                      <Input
                        className="chams-figure h-11"
                        dir="ltr"
                        value={payment.draft.chequeNumber}
                        onChange={(event) =>
                          payment.patchDraft({ chequeNumber: event.target.value })
                        }
                      />
                    </Label>
                    <Label className="flex flex-col items-stretch gap-1.5 text-start">
                      <span className="text-[11.5px] font-bold text-foreground/70">
                        {text.bank}
                      </span>
                      <Input
                        className="h-11"
                        value={payment.draft.bank}
                        onChange={(event) => payment.patchDraft({ bank: event.target.value })}
                      />
                    </Label>
                  </div>
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {text.drawer}
                    </span>
                    <Input
                      className="h-11"
                      value={payment.draft.drawer}
                      onChange={(event) => payment.patchDraft({ drawer: event.target.value })}
                    />
                  </Label>
                </div>
              )}

              {payment.draft.method === "transfer" && (
                <div className="payment-fields animate-fade-up grid gap-3 sm:grid-cols-2">
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {text.sender}
                    </span>
                    <Input
                      className="h-11"
                      value={payment.draft.senderLabel}
                      onChange={(event) => payment.patchDraft({ senderLabel: event.target.value })}
                    />
                  </Label>
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">{text.bank}</span>
                    <Input
                      className="h-11"
                      value={payment.draft.bank}
                      onChange={(event) => payment.patchDraft({ bank: event.target.value })}
                    />
                  </Label>
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {text.transferReference}
                    </span>
                    <Input
                      className="chams-figure h-11"
                      dir="ltr"
                      value={payment.draft.transferReference}
                      onChange={(event) =>
                        payment.patchDraft({ transferReference: event.target.value })
                      }
                    />
                  </Label>
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {text.statedDate}
                    </span>
                    <DatePicker
                      locale={locale}
                      label={text.statedDate}
                      max={payment.today}
                      value={payment.draft.statedDate}
                      onChange={(value) => payment.patchDraft({ statedDate: value })}
                    />
                  </Label>
                </div>
              )}

              <div className="flex flex-col gap-3.5 border-t border-border/70 pt-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {t("sp.receivedDate")}
                    </span>
                    <DatePicker
                      locale={locale}
                      label={t("sp.receivedDate")}
                      min={student.calendar.activeFrom}
                      max={payment.today}
                      value={payment.draft.receivedDate}
                      onChange={(value) => payment.patchDraft({ receivedDate: value })}
                    />
                  </Label>
                  <Label className="flex flex-col items-stretch gap-1.5 text-start">
                    <span className="text-[11.5px] font-bold text-foreground/70">
                      {t("sp.note")}{" "}
                      <span className="font-normal text-muted-2">{t("sp.optional")}</span>
                    </span>
                    <Input
                      className="h-11"
                      maxLength={500}
                      value={payment.draft.note}
                      onChange={(event) => payment.patchDraft({ note: event.target.value })}
                    />
                  </Label>
                </div>

                {payment.backdated && (
                  <div className="animate-fade-up flex items-start gap-3 rounded-md border border-warning-border bg-warning-soft px-4 py-3">
                    <span
                      aria-hidden="true"
                      className="grid size-[18px] shrink-0 place-items-center rounded-full bg-warning font-heading text-[11px] font-extrabold text-warning-foreground"
                    >
                      !
                    </span>
                    <Label className="flex flex-1 flex-col items-stretch gap-2 text-start">
                      <span className="font-heading text-xs font-bold text-warning">
                        {t("sp.backdatedTitle")}
                      </span>
                      <Input
                        className="h-11 border-warning-border bg-card"
                        placeholder={t("sp.backdatedPlaceholder")}
                        value={payment.draft.backdatingReason}
                        onChange={(event) =>
                          payment.patchDraft({ backdatingReason: event.target.value })
                        }
                      />
                    </Label>
                  </div>
                )}
              </div>
            </section>
          )}

          {payment.step === "confirmation" && (
            <section
              key="confirmation"
              className="payment-step animate-fade-up flex flex-col gap-4"
            >
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="flex items-baseline gap-2.5 border-b border-border/70 bg-surface-2 px-5 py-4">
                  <strong className="font-heading text-sm">{t("sp.confirmTitle")}</strong>
                  <span className="text-[11.5px] text-muted-foreground">{t("sp.confirmSub")}</span>
                </div>
                <div className="flex flex-col gap-3 p-5">
                  <SummaryRow label={t("sp.student")}>
                    {locale === "ar" ? student.nameAr : student.nameFr}
                  </SummaryRow>
                  <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-3 text-[12.5px] text-muted-foreground">
                    <span className="whitespace-nowrap">{t("sp.obligationsPaid")}</span>
                    <strong className="financial-document__period-pills min-w-0 font-heading text-foreground">
                      {paidPeriodLabels.map((entry) => (
                        <span className="financial-document__period-pill" key={entry.period}>
                          {entry.label}
                        </span>
                      ))}
                    </strong>
                  </div>
                  <SummaryRow label={t("sp.method")}>{text[payment.draft.method]}</SummaryRow>
                  <SummaryRow label={t("sp.receivedDate")}>
                    <DateValue value={payment.draft.receivedDate} />
                  </SummaryRow>
                  <span aria-hidden="true" className="h-px bg-border/70" />
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-heading text-sm font-bold">{t("sp.total")}</span>
                    <strong className="font-heading text-[28px] leading-none text-primary">
                      <Amount value={payment.allocatedNumeric} locale={locale} />
                    </strong>
                  </div>
                </div>
              </div>
              <p className="flex items-start gap-3 rounded-lg border border-destructive-border bg-destructive-soft px-4 py-3.5 text-xs leading-relaxed text-destructive">
                <span
                  aria-hidden="true"
                  className="grid size-[18px] shrink-0 place-items-center rounded-full bg-destructive font-heading text-[11px] font-extrabold text-white"
                >
                  !
                </span>
                {t("sp.immutableWarn")}
              </p>
              {payment.error && (
                <p
                  role="alert"
                  className="animate-shake rounded-md border border-destructive-border bg-destructive-soft px-4 py-3 text-xs text-destructive"
                >
                  {text[payment.error]}
                </p>
              )}
            </section>
          )}

          {payment.step === "document" && payment.result && (
            <section key="document" className="payment-result animate-fade-up flex flex-col gap-4">
              <div className="flex items-center gap-3 rounded-lg border border-success-border bg-success-soft px-4 py-3.5">
                <span
                  aria-hidden="true"
                  className="grid size-8 shrink-0 place-items-center rounded-full bg-success"
                >
                  <svg viewBox="0 0 18 18" fill="none" className="size-4">
                    <path
                      d="M4 9.3l3.2 3.2L14 5.8"
                      stroke="currentColor"
                      className="animate-tick text-white"
                      strokeWidth="2.3"
                      strokeLinecap="round"
                      strokeDasharray="26"
                    />
                  </svg>
                </span>
                <span className="flex flex-col gap-0.5">
                  <strong className="font-heading text-[13.5px] text-success">
                    {t(cash ? "sp.receiptIssued" : "sp.provIssued")}
                  </strong>
                  <small className="text-[11.5px] text-success">
                    {t(cash ? "sp.receiptIssuedSub" : "sp.provIssuedSub")}
                  </small>
                </span>
                <strong className="ms-auto font-heading text-[12.5px] text-success">
                  <Bidi>{payment.result.payment.reference}</Bidi>
                </strong>
              </div>

              <div className="payment-result__actions no-print mx-auto flex w-full max-w-md flex-col gap-3">
                <ReceiptLanguageSwitch
                  locale={locale}
                  value={payment.receiptLocale}
                  onChange={payment.setReceiptLocale}
                  showLabel={false}
                />
                <div className="grid grid-cols-2 gap-2.5">
                  <Button className="w-full" onClick={printFinancialDocument}>
                    <Printer />
                    {t("sp.print")}
                  </Button>
                  {whatsapp.url ? (
                    <a
                      href={whatsapp.url}
                      target="_blank"
                      rel="external noopener noreferrer"
                      className={buttonVariants({
                        variant: "outline",
                        className:
                          "w-full !border-success bg-white text-success hover:!border-success hover:!bg-success-soft hover:!text-success",
                      })}
                    >
                      <MessageCircle aria-hidden="true" />
                      {operationsText.whatsapp}
                    </a>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-success bg-white text-success hover:!border-success hover:!bg-success-soft hover:!text-success"
                      disabled={!online || !csrf || whatsapp.preparing}
                      onClick={whatsapp.prepare}
                    >
                      {whatsapp.preparing ? (
                        <LoaderCircle aria-hidden="true" className="animate-spin" />
                      ) : (
                        <MessageCircle aria-hidden="true" />
                      )}
                      {whatsapp.preparing
                        ? operationsText.whatsappPreparing
                        : operationsText.whatsappGenerate}
                    </Button>
                  )}
                </div>
                {whatsapp.url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => void whatsapp.copy()}
                  >
                    <Copy aria-hidden="true" />
                    {whatsapp.copied ? operationsText.whatsappCopied : operationsText.whatsappCopy}
                  </Button>
                )}
              </div>
              <div className="no-print mx-auto flex max-w-[62ch] flex-col items-center gap-1.5 text-center text-[10.5px] leading-relaxed">
                {whatsapp.failed && (
                  <small role="alert" className="text-destructive">
                    {operationsText.whatsappPrepareFailed}{" "}
                    <button
                      type="button"
                      className="font-bold underline underline-offset-2"
                      onClick={whatsapp.retry}
                    >
                      {operationsText.whatsappRetry}
                    </button>
                  </small>
                )}
                {whatsapp.copyFailed && (
                  <small role="alert" className="text-destructive">
                    {operationsText.whatsappCopyFailed}
                  </small>
                )}
              </div>

              {!cash && (
                <div className="flex items-center gap-3 rounded-lg border border-warning-border bg-warning-soft px-4 py-3.5">
                  <span
                    aria-hidden="true"
                    className="grid size-[30px] shrink-0 place-items-center rounded-full bg-warning font-heading text-[15px] font-extrabold text-warning-foreground"
                  >
                    ≈
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <strong className="font-heading text-[13px] text-warning">
                      {t("sp.provTitle")}
                    </strong>
                    <small className="text-[11.5px] leading-relaxed text-warning">
                      {t("sp.provBody")}
                    </small>
                  </span>
                </div>
              )}

              {queuedOffline && (
                <div className="flex items-center gap-3 rounded-lg border border-info-border bg-info-soft px-4 py-3.5">
                  <span
                    aria-hidden="true"
                    className="grid size-[30px] shrink-0 place-items-center rounded-full bg-info text-white"
                  >
                    <Clock className="size-4" />
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <strong className="font-heading text-[13px] text-info">
                      {t("sp.queuedTitle")}
                    </strong>
                    <small className="text-[11.5px] leading-relaxed text-info">
                      {t("sp.queuedBody")}
                    </small>
                  </span>
                </div>
              )}

              <div
                className="flex flex-col items-center gap-3.5 rounded-lg bg-sunken p-6"
                data-document-preview-canvas
                dir="ltr"
              >
                <div className="payment-result__document w-full max-w-[440px] animate-rise">
                  <A4DocumentPreview locale={payment.receiptLocale} spatialDirection="ltr">
                    <FinancialDocument
                      locale={payment.receiptLocale}
                      snapshot={payment.result.document}
                    />
                  </A4DocumentPreview>
                </div>
                {!cash && (
                  <p
                    className="max-w-[52ch] text-center text-[11.5px] leading-relaxed text-muted-foreground"
                    dir={locale === "ar" ? "rtl" : "ltr"}
                  >
                    {t("sp.provNote")}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="flex flex-none flex-col gap-2 border-t border-border/70 bg-surface-2 px-6 py-3">
          <div className="flex w-full items-center gap-3">
            {payment.stepNumber > 1 && payment.step !== "document" && (
              <Button variant="outline" onClick={payment.retreat}>
                <ChevronLeft className="rtl:-scale-x-100" />
                {t("sp.back")}
              </Button>
            )}
            <div className="ms-auto flex items-center gap-4">
              {payment.stepNumber < 4 && (
                <span className="flex flex-col items-end gap-1">
                  <small className="text-[11px] text-muted-foreground">
                    {t("sp.runningTotal")}
                  </small>
                  <strong className="font-heading text-xl leading-none">
                    <Amount value={running} locale={locale} />
                  </strong>
                </span>
              )}
              {payment.step === "document" ? (
                <Button onClick={close}>{t("sp.finish")}</Button>
              ) : payment.step === "confirmation" ? (
                <Button
                  variant="success"
                  size="lg"
                  disabled={!payment.canAdvance}
                  onClick={() => void payment.finalize()}
                >
                  {payment.busy ? text.writing : t("sp.confirmPay")}
                </Button>
              ) : (
                <Button size="lg" disabled={!payment.canAdvance} onClick={payment.advance}>
                  {t("sp.next")}
                </Button>
              )}
            </div>
          </div>
          {yearContext && (
            <strong className="w-full rounded-lg border border-warning-border bg-warning-soft px-4 py-2.5 text-center font-heading text-[11px] text-warning">
              {t(
                yearContext.role === "next"
                  ? "sp.nextYearPaymentNotice"
                  : "sp.historicalYearPaymentNotice",
              )}{" "}
              <Bidi>{yearContext.academicYear}</Bidi>
            </strong>
          )}
          {!yearContext && (
            <strong className="w-full rounded-lg border border-teal-100 bg-teal-50 px-4 py-2.5 text-center font-heading text-[11px] text-primary-strong">
              {t("sp.currentYearPaymentNotice")} <Bidi>{student.calendar.academicYear}</Bidi>
            </strong>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
